import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Route,
  ArrowLeft,
  Lock,
  ChevronRight,
  Loader2,
  Crown,
  CheckCircle,
  Circle,
  Rocket,
  Target,
  DollarSign,
  TrendingUp,
  Cpu,
  Globe
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { useAuth } from '../context/AuthContext';
import { api } from '../lib/api';
import { toast } from 'sonner';

const PathwaysPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [pathways, setPathways] = useState([]);
  const [loading, setLoading] = useState(true);

  const effectiveTier = user?.effective_tier || user?.tier || 'free';

  useEffect(() => {
    loadPathways();
  }, []);

  const loadPathways = async () => {
    try {
      const response = await api.getPathways();
      setPathways(response.data.pathways || []);
    } catch (error) {
      console.error('Error loading pathways:', error);
      toast.error('Could not load pathways');
    } finally {
      setLoading(false);
    }
  };

  const getPathwayIcon = (pathwayId) => {
    const icons = {
      'zero_to_one': Rocket,
      'mvp_builder': Target,
      'funding_ready': DollarSign,
      'gtm_launch': TrendingUp,
      'growth_engine': TrendingUp,
      'ai_founder': Cpu,
      'global_expansion': Globe
    };
    return icons[pathwayId] || Route;
  };

  const getPathwayColor = (pathwayId) => {
    const colors = {
      'zero_to_one': 'from-ocean-blue to-sky-blue',
      'mvp_builder': 'from-mint to-emerald-400',
      'funding_ready': 'from-coral to-orange-400',
      'gtm_launch': 'from-purple-500 to-violet-400',
      'growth_engine': 'from-pink-500 to-rose-400',
      'ai_founder': 'from-cyan-500 to-blue-400',
      'global_expansion': 'from-indigo-500 to-blue-500'
    };
    return colors[pathwayId] || 'from-gray-500 to-gray-400';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-ocean-blue" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-background/95 backdrop-blur-md border-b border-border">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="icon"
                onClick={() => navigate('/dashboard')}
                data-testid="back-to-dashboard"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="flex items-center gap-2">
                <Route className="w-6 h-6 text-ocean-blue" />
                <span className="font-bold text-foreground">Pathways</span>
              </div>
            </div>
            <Badge variant="outline" className={`capitalize ${
              effectiveTier === 'pro' ? 'border-coral text-coral' :
              effectiveTier === 'basic' ? 'border-ocean-blue text-ocean-blue' :
              'border-muted-foreground text-muted-foreground'
            }`}>
              {effectiveTier === 'pro' && <Crown className="w-3 h-3 mr-1" />}
              {effectiveTier} tier
            </Badge>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Hero */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">
            Learning Pathways
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Structured learning tracks designed for specific founder goals. Each pathway combines modules and labs for focused skill building.
          </p>
        </motion.div>

        {/* Pathways Grid */}
        <div className="grid md:grid-cols-2 gap-6">
          {pathways.map((pathway, index) => {
            const PathwayIcon = getPathwayIcon(pathway.id);
            const gradientColor = getPathwayColor(pathway.id);
            const isLocked = pathway.tier === 'pro' && effectiveTier !== 'pro';
            const completedModules = pathway.modules?.filter(m => 
              user?.modules_completed?.includes(m)
            ).length || 0;
            const totalModules = pathway.modules?.length || 1;
            const progressPercent = (completedModules / totalModules) * 100;

            return (
              <motion.div
                key={pathway.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <Card 
                  className={`h-full rounded-2xl overflow-hidden ${
                    isLocked ? 'opacity-80' : 'hover:shadow-xl transition-shadow'
                  }`}
                  data-testid={`pathway-${pathway.id}`}
                >
                  <div className={`h-2 bg-gradient-to-r ${gradientColor}`} />
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${gradientColor} flex items-center justify-center`}>
                        {isLocked ? (
                          <Lock className="w-7 h-7 text-white" />
                        ) : (
                          <PathwayIcon className="w-7 h-7 text-white" />
                        )}
                      </div>
                      <Badge className={pathway.tier === 'pro' ? 'bg-coral' : 'bg-ocean-blue'}>
                        {pathway.tier === 'pro' && <Crown className="w-3 h-3 mr-1" />}
                        {pathway.tier === 'pro' ? 'Pro' : 'Basic'}
                      </Badge>
                    </div>
                    <CardTitle className="mt-4">{pathway.title}</CardTitle>
                    <CardDescription className="text-base">{pathway.description}</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Progress */}
                    {!isLocked && (
                      <div>
                        <div className="flex justify-between text-sm text-muted-foreground mb-2">
                          <span>Progress</span>
                          <span>{completedModules}/{totalModules} modules</span>
                        </div>
                        <Progress value={progressPercent} className="h-2" />
                      </div>
                    )}

                    {/* Modules in pathway */}
                    <div className="space-y-2">
                      <p className="text-sm font-medium text-foreground">Includes:</p>
                      <div className="flex flex-wrap gap-2">
                        {pathway.modules?.slice(0, 4).map((moduleId) => (
                          <Badge key={moduleId} variant="secondary" className="text-xs">
                            {moduleId.replace('module_', 'M')}
                          </Badge>
                        ))}
                        {pathway.modules?.length > 4 && (
                          <Badge variant="secondary" className="text-xs">
                            +{pathway.modules.length - 4} more
                          </Badge>
                        )}
                      </div>
                    </div>

                    {/* Action */}
                    <div className="pt-2">
                      {isLocked ? (
                        <Button 
                          variant="outline" 
                          className="w-full rounded-full"
                          onClick={() => navigate('/certification')}
                        >
                          <Lock className="w-4 h-4 mr-2" />
                          Upgrade to Pro
                        </Button>
                      ) : (
                        <Button 
                          className={`w-full rounded-full bg-gradient-to-r ${gradientColor} text-white hover:opacity-90`}
                          onClick={() => navigate(`/curriculum/${pathway.modules?.[0] || 'module_0'}`)}
                        >
                          {progressPercent > 0 ? 'Continue' : 'Start Pathway'}
                          <ChevronRight className="w-4 h-4 ml-1" />
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </div>
      </main>
    </div>
  );
};

export default PathwaysPage;
